import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, signal, ViewChild } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { ItemPlainType, S25ObjectDetailsService } from "./s25.object.details.service";
import { S25Util } from "../../util/s25-util";
import { SpaceService } from "../../services/space.service";
import { S25Const } from "../../util/s25-const";
import { Bind } from "../../decorators/bind.decorator";
import { DropDownItem } from "../../pojo/DropDownItem";
import { S25EditableDurationComponent } from "../s25-editable/s25-editable-duration/s25.editable.duration.component";
import { BuildingService } from "../../services/building.service";
import { StateService } from "../../services/state.service";
import { Waitlist } from "../../pojo/Waitlist";
import { TelemetryService } from "../../services/telemetry.service";
import { CapitalizeBoundariesPipe } from "../../pipes/s25.pipes";

@TypeManagerDecorator("s25-ng-object-details-item-plain")
@Component({
    selector: "s25-ng-object-details-item-plain",
    template: `
        @switch (type) {
            @case ("scheduler") {
                @if (canEdit) {
                    <s25-contact-dropdown
                        [usersOnly]="true"
                        [(value)]="dropdownVal"
                        (valueChange)="update()"
                        [ariaLabel]="'Scheduler'"
                    />
                    <ng-container [ngTemplateOutlet]="dropdownRemoveButton"></ng-container>
                } @else {
                    @if (dropdownVal?.itemId) {
                        <s25-item
                            [itemId]="dropdownVal.itemId"
                            [itemTypeId]="dropdownVal.itemTypeId"
                            [itemName]="dropdownVal.itemName"
                            [includeTypeIcon]="true"
                        ></s25-item>
                    } @else {
                        none
                    }
                }
            }
            @case ("partition") {
                @if (canEdit) {
                    <s25-partition-dropdown
                        [(value)]="dropdownVal"
                        (valueChange)="update()"
                        [ariaLabel]="'Partition'"
                    />
                    <ng-container [ngTemplateOutlet]="dropdownRemoveButton"></ng-container>
                } @else {
                    <ng-container [ngTemplateOutlet]="readonlyDropdownName"></ng-container>
                }
            }
            @case ("ratio") {
                <s25-ng-editable-number
                    [(val)]="value"
                    [min]="0"
                    [max]="100"
                    [readOnly]="!canEdit"
                    [ariaLabel]="'Fill Ratio Percentage'"
                    [hasCommit]="true"
                    [onCommit]="update"
                    [allowEmpty]="true"
                ></s25-ng-editable-number>
            }
            @case ("express") {
                @if (canEdit) {
                    <label>
                        <span class="label-text">Default Event Type</span>
                        <s25-ng-dropdown-search-criteria
                            [type]="'eventTypes'"
                            [value]="dropdownVal"
                            [ariaLabel]="'Default Event Type'"
                            (valueChange)="tempExpressData.dropdownData = $event; checkExpressValsChanged()"
                        />
                    </label>
                    <label>
                        <span class="label-text">Maximum Event Duration</span>
                        <s25-ng-editable-duration
                            [val]="stringVal"
                            [commitButtonText]="'Apply'"
                            (valChange)="tempExpressData.duration = $event; checkExpressValsChanged()"
                        ></s25-ng-editable-duration>
                        @if (errorMsg()) {
                            <p class="ngRed">{{ errorMsg() }}</p>
                        }
                    </label>
                    <div class="button-group">
                        <s25-ng-button
                            [type]="'outline'"
                            [onClick]="update"
                            [disabled]="disableSaveBtn()"
                            [disabledReason]="'Please update the event type or duration to save'"
                            >Save</s25-ng-button
                        >
                        @if (dropdownVal?.itemId) {
                            <s25-ng-button [type]="'danger--outline'" [onClick]="disableExpressSched"
                                >Disable</s25-ng-button
                            >
                        }
                    </div>
                }
            }
            @case ("building") {
                @if (canEdit) {
                    <s25-ng-dropdown-search-criteria
                        [type]="'buildings'"
                        [(value)]="dropdownVal"
                        [ariaLabel]="'Building'"
                        (valueChange)="update()"
                    />
                    <ng-container [ngTemplateOutlet]="dropdownRemoveButton"></ng-container>
                } @else {
                    <ng-container [ngTemplateOutlet]="readonlyDropdownName"></ng-container>
                }
                @if (imageData?.imageId) {
                    <s25-ng-image
                        [model]="{
                            imageId: imageData.imageId,
                            imageUrl: $safeNavigationMigration(imageData?.imageUrl),
                        }"
                    ></s25-ng-image>
                }
            }
            @case ("tasks") {
                <div class="task-wrapper">
                    @for (task of taskData; track $index) {
                        <label class="task-item">
                            {{ task.label }}
                            <p>{{ task.value }}</p>
                        </label>
                    }
                </div>
                <button class="c-textButton" (click)="navToTaskList()">View this event's Task List</button>
            }
            @case ("waitlist") {
                <div class="waitlist-row">
                    @if (canEdit) {
                        <s25-generic-dropdown
                            [items]="waitlistingItem"
                            [(value)]="dropdownVal"
                            [searchEnabled]="false"
                            [ariaLabel]="'Waitlist'"
                            (valueChange)="update()"
                        ></s25-generic-dropdown>
                    } @else {
                        <p>{{ waitlistName | titlecase }}</p>
                    }
                    <s25-ng-info-message [isPopover]="true">
                        <div class="spacer">
                            <b>Do Not Allow</b> - Overlapping requests will not be grouped and displayed as a waitlist.
                        </div>
                        <div>
                            <b>Request Only</b> - Overlapping requests are grouped and displayed in the order they were
                            received.
                        </div>
                        <div>
                            <b>Requests and Assignments</b> - When an existing assignment is already in place,
                            additional requests for the location will join the waitlist.
                        </div>
                    </s25-ng-info-message>
                </div>
            }
        }

        <ng-template #dropdownRemoveButton>
            @if (dropdownVal?.itemId) {
                <s25-ng-button [type]="'danger--transparent'" (clicked)="isDeleteAction.set(true)" [onClick]="update"
                    >Remove</s25-ng-button
                >
            }
        </ng-template>

        <ng-template #readonlyDropdownName>
            <p>{{ (dropdownVal?.itemName | capitalizeBoundaries) || "none" }}</p>
        </ng-template>
    `,
    host: {
        "[class.tasks-panel]": "type === 'tasks'",
    },
    styles: `
        :host {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
            padding: 1rem;

            label {
                width: 100%;
            }

            .button-group {
                display: flex;
                gap: 10px;
            }

            s25-contact-dropdown,
            s25-partition-dropdown,
            s25-ng-dropdown-search-criteria,
            s25-ng-editable-number {
                display: block;
                width: 100%;
            }

            .waitlist-row {
                display: flex;
                align-items: center;
                gap: 10px;
                width: 100%;

                s25-generic-dropdown {
                    flex: 1;
                }
            }

            .spacer {
                padding-bottom: 5px;
            }

            &.tasks-panel {
                flex-direction: row;

                .task-wrapper {
                    width: 10em;
                    display: flex;
                    flex-direction: column;
                    gap: 5px;

                    .task-item {
                        display: flex;
                        justify-content: space-between;
                    }
                }
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25ObjectDetailsItemPlainComponent implements OnInit {
    @Input({ required: true }) itemId: number;
    @Input({ required: true }) itemTypeId: number;
    @Input({ required: true }) type: ItemPlainType;
    @Input({ required: true }) canEdit: boolean;
    @Input({ required: true }) value: DropDownItem | number | string;
    @Input() altVal?: { imageId: number; imageUrl: string } | string;

    @ViewChild(S25EditableDurationComponent) editableDurationComp: S25EditableDurationComponent;

    isDeleteAction = signal(false);
    errorMsg = signal("");
    disableSaveBtn = signal(true);
    tempExpressData: { dropdownData?: DropDownItem; duration?: string } = {};
    tempVal: number;

    waitlistingItem = Waitlist.WaitlistLevels;

    constructor(private cd: ChangeDetectorRef) {}

    async ngOnInit() {
        // tempData for avoiding service calls if data hasn't updated
        if (this.type === "express" && this.altVal) {
            this.tempExpressData = {
                dropdownData: this.dropdownVal,
                duration: this.altVal as string,
            };
        } else {
            this.tempVal = (this.type === "ratio" ? this.value : this.dropdownVal?.itemId) as number;
        }
    }

    @Bind
    async update() {
        switch (this.type) {
            case "scheduler":
            case "express":
                return this.updateSpace();
            case "ratio":
            case "partition":
            case "building":
                return this.updateSpaceSimple();
            case "waitlist":
                return this.updateSpace();
        }
    }

    async updateSpaceSimple() {
        const { idProp } = S25ObjectDetailsService.PayloadPropConst[this.type];
        const value = this.type === "ratio" ? this.value : this.dropdownVal.itemId;
        if (!this.isDeleteAction() && value === this.tempVal) return;
        const [_, error] = await S25Util.Maybe(
            SpaceService.microUpdate({
                id: this.itemId,
                [idProp]: this.isDeleteAction() || !S25Util.isDefined(value) ? "" : value,
            }),
        );
        if (error) return console.error(error);
        if (this.isDeleteAction()) this.onDelete();
        else {
            this.tempVal = (this.type === "ratio" ? this.value : this.dropdownVal.itemId) as number;
        }
        if (this.type === "building") {
            this.imageData = await this.updateBuildingImage(this.dropdownVal?.itemId as number);
            this.cd.markForCheck();
        }
    }

    async updateSpace() {
        if (!this.validate()) return;
        const { typeProp, idProp, altProp, hasAltVal } = S25ObjectDetailsService.PayloadPropConst[this.type];
        const { propVal, altVal } = this.getPropVals();
        let payload = {};
        if (this.type === "waitlist") {
            payload = {
                waitlist: propVal,
            };

            const telemetryLabel = Waitlist.WaitlistLevels.find((l) => l.itemId === propVal)?.telemetryLabel;
            if (telemetryLabel && propVal !== "request") {
                TelemetryService.sendWithSub("Waitlist", "Settings", telemetryLabel);
            }
        } else {
            payload = {
                [typeProp]: [
                    {
                        [idProp]: propVal,
                        ...(!this.isDeleteAction() &&
                            hasAltVal && {
                                [altProp]: altVal,
                            }),
                    },
                ],
            };
        }

        const [_, error] = await S25Util.Maybe(
            SpaceService.microUpdate({
                id: this.itemId,
                ...(this.isDeleteAction() ? { remove: payload } : payload),
            }),
        );
        if (error) return console.error(error);
        this.disableSaveBtn.set(true);
        if (this.isDeleteAction()) this.onDelete();
        else if (this.type === "express") {
            this.dropdownVal = this.tempExpressData.dropdownData;
            this.altVal = this.tempExpressData.duration;
        }
    }

    onDelete() {
        this.value = null;
        this.disableSaveBtn.set(true);
        this.isDeleteAction.set(false);
        this.errorMsg.set("");
        this.cd.markForCheck();
    }

    @Bind
    async disableExpressSched() {
        const [_, error] = await S25Util.Maybe(
            SpaceService.microUpdate({ id: this.itemId, remove: { directSchedule: "" } }),
        );
        if (error) console.error(error);
        this.tempExpressData &&= { duration: "P0DT00H00M", dropdownData: { itemId: undefined } };
        this.altVal = "P0DT00H00M";
        this.onDelete();
        this.editableDurationComp.candidateVal = { minutes: 0, hours: 0, days: 0 };
        this.editableDurationComp.val = "P0DT00H00M";
        this.editableDurationComp.refresh();
    }

    validate() {
        this.errorMsg.set("");
        switch (this.type) {
            case "express": {
                if (!this.tempExpressData?.dropdownData.itemId) {
                    this.errorMsg.set("Please select a default event type");
                    return false;
                }
                return true;
            }
            default:
                return true;
        }
    }

    getPropVals() {
        switch (this.type) {
            case "express":
                return {
                    propVal: this.tempExpressData.dropdownData.itemId,
                    altVal: S25Util.ISODurationToMinutes(this.tempExpressData.duration),
                };
            case "scheduler":
                return {
                    propVal: S25Const.schedulerRole.location,
                    altVal: this.dropdownVal.itemId,
                };
            default:
                return {
                    propVal: this.dropdownVal.itemId,
                    altVal: this.altVal,
                };
        }
    }

    checkExpressValsChanged() {
        this.disableSaveBtn.set(
            this.tempExpressData?.dropdownData.itemId === this.dropdownVal?.itemId &&
                this.tempExpressData?.duration === this.altVal,
        );
    }

    async updateBuildingImage(buildingId: number) {
        this.imageData = null;
        this.cd.markForCheck();
        if (!buildingId) return null;
        const { imageId, url } = await BuildingService.getImageUrl(buildingId);
        if (imageId && url) {
            return { imageId: imageId, imageUrl: url };
        }
        return null;
    }

    navToTaskList() {
        return StateService.gotoItem({ itemTypeId: this.itemTypeId, itemId: this.itemId, forceView: "tasks" });
    }

    get dropdownVal() {
        return this.value as DropDownItem;
    }

    set dropdownVal(val: DropDownItem) {
        this.value = val;
    }

    set imageData(val: { imageId: number; imageUrl: string }) {
        this.altVal = val;
    }

    get taskData() {
        return (this.value ?? []) as TaskData[];
    }

    get imageData() {
        return this.altVal as { imageId: number; imageUrl: string };
    }

    get waitlistName() {
        return this.waitlistingItem.find((w) => w.itemId === this.dropdownVal?.itemId)?.itemName ?? "none";
    }

    get stringVal() {
        return this.altVal as string;
    }
}

export type TaskData = {
    label: string;
    value: string;
};
